import { FC, ReactElement } from "react";
import { Button, ButtonProps, Grid, SxProps, Typography } from "@mui/material";
import {
  CheckState,
  ComplianceFrameworkState,
  ControlState,
} from "~/operations";
import {
  Edit as EditIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { DisabledIcon, FalsePositiveIcon, WorkaroundIcon } from "~/icons";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";

type ExceptionFlagProps = {
  state: ControlState | ComplianceFrameworkState;
  onClick: ButtonProps["onClick"];
  buttonProps?: {
    sx: SxProps;
  };
};

export function FrameworkHeaderExceptionFlag({
  state,
  onClick,
  buttonProps = { sx: {} },
}: ExceptionFlagProps) {
  const getStatus = (
    state: ControlState | ComplianceFrameworkState | CheckState,
  ) => {
    if (
      state === ControlState.Active ||
      state === ComplianceFrameworkState.Active ||
      state === CheckState.Active
    ) {
      return "active";
    } else if (
      state === ControlState.Snoozed ||
      state === ComplianceFrameworkState.Preview
    ) {
      return "snoozed";
    } else if (state === ControlState.RiskAccepted) {
      return "risk accepted";
    } else if (state === ControlState.OutOfScope) {
      return "out of scope";
    } else if (state === ControlState.FalsePositive) {
      return "false positive";
    } else if (state === ControlState.Workaround) {
      return "workaround";
    } else {
      return "disabled";
    }
  };

  if (getStatus(state) === "active") {
    return <SetExceptionButton onClick={onClick} {...buttonProps} />;
  }

  function getFlagData(state: ControlState | ComplianceFrameworkState): {
    label: string;
    icon: ReactElement;
    tagline: string;
  } {
    const status = getStatus(state);

    switch (status) {
      case "snoozed":
        return {
          label: "SNOOZED",
          tagline: "Run, do not score - optional time limit",
          icon: <ThumbUpOffAltIcon />,
        };
      case "risk accepted":
        return {
          label: "RISK ACCEPTED",
          tagline: "Run, do not score - optional time limit",
          icon: <ThumbUpOffAltIcon />,
        };
      case "out of scope":
        return {
          label: "OUT OF SCOPE",
          tagline: "Out Of Scope",
          icon: <VisibilityOffIcon />,
        };
      case "false positive":
        return {
          label: "FALSE POSITIVE",
          tagline: "Run, do not score - optional time limit",
          icon: <FalsePositiveIcon />,
        };
      case "workaround":
        return {
          label: "WORKAROUND",
          tagline: "Run, do not score - optional time limit",
          icon: <WorkaroundIcon />,
        };
      case "disabled":
      default:
        return {
          label: "DISABLED",
          tagline: "Do not run or score",
          icon: <DisabledIcon />,
        };
    }
  }

  const flagData = getFlagData(state);

  return (
    <Grid
      container
      sx={{
        padding: 1,
        minWidth: 211,
        maxWidth: 211,
        columnGap: 1,
        border: "1px solid",
        borderColor: "text.secondary",
        borderRadius: 1,
        color: "text.secondary",
        alignSelf: "flex-start",
      }}
      data-test-id="exception-flag"
    >
      <Grid size="auto">{flagData.icon}</Grid>
      <Grid container sx={{ flexDirection: "column" }} size="grow">
        <Grid>
          <Typography
            sx={{
              fontSize: (theme) => theme.spacing(1.75),
              fontWeight: 600,
              textTransform: "uppercase",
              lineHeight: "normal",
            }}
            data-test-id="exception-flag-status"
          >
            {flagData.label}
          </Typography>
        </Grid>
        <Grid>
          <Typography
            sx={{
              fontSize: (theme) => theme.spacing(1.25),
            }}
          >
            {flagData.tagline}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

const SetExceptionButton: FC<ButtonProps> = (props) => {
  return (
    <Button
      {...props}
      variant="outlined"
      startIcon={<EditIcon sx={{ fontSize: (theme) => theme.spacing(2.25) }} />}
      sx={{
        color: "text.secondary",
        fontSize: (theme) => theme.spacing(1.625),
        ...props.sx,
      }}
      data-test-id="set-exception-button"
    >
      Set Exception
    </Button>
  );
};
